import { Component, Element, Event, Host, h, Method, EventEmitter, Listen, State } from '@stencil/core';
import focusLock from 'dom-focus-lock';
import { disableBodyScroll, enableBodyScroll } from '../../utils/utils';

@Component({
  tag: 'me-menu',
  styleUrl: 'me-menu.scss',
  shadow: false,
})
export class MeMenu {

  private static nextMenuID = 1;
  private readonly menuID = MeMenu.nextMenuID++;
  private readonly labelID = 'me-menu-label-' + this.menuID;

  @Element() host: HTMLMeMenuElement;

  @Event() meMenuOpen: EventEmitter<HTMLMeMenuElement>;
  @Event() meMenuClose: EventEmitter<HTMLMeMenuElement>;

  @State() private active = false;

  private isAnimated = false;
  private isFocusLockActive = false;
  private closeButton?: HTMLMeButtonElement;
  private scrollContainer?: HTMLElement;

  @Listen('meNavbarMenuButtonClick', { target: 'window' })
  async handleNavbarMenuButtonClick() {
    await this.open();
  }

  @Listen('meBackdropClose', { target: 'window' })
  async handleBackdropClose() {
    await this.close();
  }

  @Listen('transitionend')
  handleTransitionEnd(event) {
    if (event.target !== this.host) { return; }

    this.manageFocus();

    if (!this.active) {
      this.scrollContainer.scroll({ top: 0, behavior: 'auto' });
    }
  }

  componentDidLoad() {
    this.isAnimated = true;
  }

  render() {
    const classList = [
      'me-menu',
      this.active ? 'me-menu--active' : null,
      this.isAnimated ? 'me-menu--animated' : null,
    ].filter(c => !!c).join(' ');

    return (
      <Host class={classList} role="dialog" aria-labelledby={this.labelID}>
        <header class="me-menu__header">
          <me-headline variant="options" tag="h4">
           <span id={this.labelID}>Menü</span>
          </me-headline>
          <me-button
            ref={el => this.closeButton = el}
            variant="link"
            onClick={this.close.bind(this)}
          >
            <i class="fa fa-xmark fa-xl" />
          </me-button>
        </header>
        <div class="me-menu__scroll-container" ref={el => this.scrollContainer = el}>
          <nav class="me-menu__nav">
            <slot />
          </nav>
        </div>
      </Host>
    );
  }

  @Method()
  async toggle() {
    this.active
      ? await this.close()
      : await this.open();
  }

  @Method()
  async open() {
    if (this.active || !this.scrollContainer) { return; }

    disableBodyScroll(this.scrollContainer, { reserveScrollBarGap: true });

    this.active = true;

    this.meMenuOpen.emit(this.host);
  }

  @Method()
  async close() {
    if (!this.active || !this.scrollContainer) { return; }

    enableBodyScroll(this.scrollContainer);

    this.active = false;

    this.meMenuClose.emit(this.host);
  }

  /**
   * Manages focus
   *
   * @see https://developer.mozilla.org/en-US/docs/Web/Accessibility/ARIA/Roles/dialog_role#required_javascript_features
   */
  private manageFocus() {
    if (this.active && !this.isFocusLockActive) {
      this.isFocusLockActive = true;
      this.closeButton.focus();
      focusLock.on(this.host);
    } else if (!this.active && this.isFocusLockActive) {
      focusLock.off(this.host);
      this.isFocusLockActive = false;
    }
  }

}
